import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchberichten, deleteberichten } from "../Reducers/actions";
import { useSelector, useDispatch } from "react-redux";
import { message } from "../../../Reducers/loading/actions";

const ModernBerichten = () => {
  const berichten = useSelector((state) => state.cms.berichten.items);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("alle");
  const [selectedStatus, setSelectedStatus] = useState("alle");
  const [viewMode, setViewMode] = useState("grid");

  // Berichtcategorieën
  const categories = [
    { value: "alle", label: "Alle categorieën", icon: "fas fa-list", color: "secondary" },
    { value: "nieuws", label: "Nieuws", icon: "fas fa-newspaper", color: "primary" },
    { value: "casos", label: "Casos", icon: "fas fa-star", color: "warning" },
    { value: "evenementen", label: "Evenementen", icon: "fas fa-calendar-alt", color: "success" },
    { value: "aankondigingen", label: "Aankondigingen", icon: "fas fa-bullhorn", color: "info" },
    { value: "activiteiten", label: "Activiteiten", icon: "fas fa-play-circle", color: "danger" }
  ];

  const statuses = [
    { value: "alle", label: "Alle statussen" },
    { value: "publish", label: "Gepubliceerd" },
    { value: "draft", label: "Concept" },
    { value: "private", label: "Privé" },
    { value: "pending", label: "In afwachting" }
  ];

  useEffect(() => {
    setLoading(true);
    dispatch(fetchberichten()).finally(() => {
      setLoading(false);
    });
  }, [dispatch]);

  // Filter berichten
  const filteredBerichten = berichten ? berichten.filter(bericht => {
    const matchesSearch = !searchTerm || 
      bericht.post_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bericht.post_excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === "alle" || 
      bericht.post_category?.toLowerCase() === selectedCategory;
    
    const matchesStatus = selectedStatus === "alle" || 
      bericht.post_status === selectedStatus;

    return matchesSearch && matchesCategory && matchesStatus;
  }) : [];

  // Delete bericht
  const handleDelete = async (id, title) => {
    if (window.confirm(`Weet je zeker dat je "${title}" wilt verwijderen?`)) {
      try {
        await dispatch(deleteberichten(id));
        dispatch(message(3000, "success", "Bericht succesvol verwijderd"));
        dispatch(fetchberichten());
      } catch (error) {
        dispatch(message(3000, "error", "Fout bij verwijderen bericht"));
      }
    }
  };

  // Get category info
  const getCategoryInfo = (categoryValue) => {
    return categories.find(cat => cat.value === categoryValue) || categories[0];
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case "publish": return "success";
      case "draft": return "warning";
      case "private": return "secondary";
      case "pending": return "info";
      default: return "secondary";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Geen datum";
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Statistics
  const stats = [
    {
      title: "Totaal Berichten",
      value: berichten ? berichten.length : 0,
      icon: "fas fa-newspaper",
      color: "primary"
    },
    {
      title: "Gepubliceerd",
      value: berichten ? berichten.filter(b => b.post_status === "publish").length : 0,
      icon: "fas fa-check-circle",
      color: "success"
    },
    {
      title: "Concepten",
      value: berichten ? berichten.filter(b => b.post_status === "draft").length : 0,
      icon: "fas fa-edit",
      color: "warning"
    },
    {
      title: "In Afwachting",
      value: berichten ? berichten.filter(b => b.post_status === "pending").length : 0,
      icon: "fas fa-clock",
      color: "info"
    }
  ];

  if (loading) {
    return (
      <div className="content-page">
        <div className="modern-cms-berichten">
          <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Laden...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-page">
      <div className="modern-cms-berichten">
        {/* Header */}
        <div className="berichten-header">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h1>
                <i className="fas fa-newspaper me-3"></i>
                Berichten Beheer
              </h1>
              <p>
                Beheer al uw berichten, nieuws en aankondigingen vanuit één centrale locatie
              </p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/cms/berichten/new" className="btn btn-primary">
                <i className="fas fa-plus me-2"></i>
                Nieuw Bericht
              </Link>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          {stats.map((stat, index) => (
            <div key={index} className="col-lg-3 col-md-6">
              <div className={`card stats-card bg-${stat.color}`}>
                <div className="card-body d-flex align-items-center">
                  <div className="icon-container me-3">
                    <i className={stat.icon}></i>
                  </div>
                  <div>
                    <div className="h4 text-white mb-0">{stat.value}</div>
                    <div className="small text-white-50">{stat.title}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row align-items-end">
              <div className="col-lg-4">
                <label className="form-label">Zoeken</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Zoek in titel of inhoud..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <label className="form-label">Categorie</label>
                <select
                  className="form-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-2">
                <label className="form-label">Weergave</label>
                <div className="btn-group w-100">
                  <button
                    className={`btn btn-outline-secondary ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <i className="fas fa-th"></i>
                  </button>
                  <button
                    className={`btn btn-outline-secondary ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <i className="fas fa-list"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {filteredBerichten.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
            <h4>Geen berichten gevonden</h4>
            <p>Er zijn nog geen berichten aangemaakt of uw zoekcriteria leveren geen resultaten op.</p>
            <Link to="/cms/berichten/new" className="btn btn-primary">
              <i className="fas fa-plus me-2"></i>
              Eerste Bericht Aanmaken
            </Link>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="row">
                {filteredBerichten.map((bericht, index) => {
                  const categoryInfo = getCategoryInfo(bericht.post_category);
                  return (
                    <div key={index} className="col-lg-4 col-md-6">
                      <div className="card bericht-card">
                        {bericht.post_image && (
                          <img
                            src={bericht.post_image}
                            className="card-img-top"
                            alt={bericht.post_title}
                            style={{ height: '200px', objectFit: 'cover' }}
                          />
                        )}
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <i className={`${categoryInfo.icon} text-${categoryInfo.color} me-2`}></i>
                            <span className={`badge bg-${categoryInfo.color}`}>
                              {categoryInfo.label}
                            </span>
                          </div>
                          <span className={`badge bg-${getStatusColor(bericht.post_status)}`}>
                            {statuses.find(s => s.value === bericht.post_status)?.label || bericht.post_status}
                          </span>
                        </div>
                        <div className="card-body">
                          <h5 className="card-title">{bericht.post_title}</h5>
                          <p className="card-text">{bericht.post_excerpt}</p>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <small className="text-muted">
                              <i className="fas fa-calendar me-1"></i>
                              {formatDate(bericht.post_date)}
                            </small>
                            {bericht.post_author && (
                              <small className="text-muted">
                                <i className="fas fa-user me-1"></i>
                                {bericht.post_author}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="card-footer">
                          <div className="d-flex gap-2">
                            <Link
                              to={`/cms/berichten/edit/${bericht._id}`}
                              className="btn btn-outline-primary btn-sm"
                            >
                              <i className="fas fa-edit me-1"></i>
                              Bewerken
                            </Link>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDelete(bericht._id, bericht.post_title)}
                            >
                              <i className="fas fa-trash me-1"></i>
                              Verwijderen
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="card">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Titel</th>
                        <th>Categorie</th>
                        <th>Status</th>
                        <th>Auteur</th>
                        <th>Datum</th>
                        <th>Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBerichten.map((bericht, index) => {
                        const categoryInfo = getCategoryInfo(bericht.post_category);
                        return (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                {bericht.post_image && (
                                  <img
                                    src={bericht.post_image}
                                    alt={bericht.post_title}
                                    className="me-3"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                                  />
                                )}
                                <div>
                                  <div className="fw-semibold">{bericht.post_title}</div>
                                  <small className="text-muted">{bericht.post_excerpt}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge bg-${categoryInfo.color}`}>
                                <i className={`${categoryInfo.icon} me-1`}></i>
                                {categoryInfo.label}
                              </span>
                            </td>
                            <td>
                              <span className={`badge bg-${getStatusColor(bericht.post_status)}`}>
                                {statuses.find(s => s.value === bericht.post_status)?.label || bericht.post_status}
                              </span>
                            </td>
                            <td>{bericht.post_author || '-'}</td>
                            <td>{formatDate(bericht.post_date)}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <Link
                                  to={`/cms/berichten/edit/${bericht._id}`}
                                  className="btn btn-outline-primary btn-sm"
                                >
                                  <i className="fas fa-edit"></i>
                                </Link>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(bericht._id, bericht.post_title)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ModernBerichten;